'use client';
import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts';
import { Sliders, MapPin, Calendar, ChevronDown, ChevronUp, Zap } from 'lucide-react';

const ALL_COUNTIES = [
  { id: 'montserrado', name: 'Montserrado', properties: 180000, currentRate: 0.18, avgTaxUSD: 420 },
  { id: 'nimba', name: 'Nimba', properties: 72000, currentRate: 0.08, avgTaxUSD: 210 },
  { id: 'bong', name: 'Bong', properties: 58000, currentRate: 0.07, avgTaxUSD: 185 },
  { id: 'lofa', name: 'Lofa', properties: 48000, currentRate: 0.06, avgTaxUSD: 160 },
  { id: 'margibi', name: 'Margibi', properties: 42000, currentRate: 0.22, avgTaxUSD: 310 },
  { id: 'grand-bassa', name: 'Grand Bassa', properties: 38000, currentRate: 0.19, avgTaxUSD: 290 },
  { id: 'grand-kru', name: 'Grand Kru', properties: 22000, currentRate: 0.04, avgTaxUSD: 140 },
  { id: 'sinoe', name: 'Sinoe', properties: 24000, currentRate: 0.05, avgTaxUSD: 150 },
  { id: 'rivercess', name: 'Rivercess', properties: 18000, currentRate: 0.04, avgTaxUSD: 130 },
  { id: 'gbarpolu', name: 'Gbarpolu', properties: 16000, currentRate: 0.03, avgTaxUSD: 120 },
  { id: 'grand-cape-mount', name: 'Grand Cape Mount', properties: 20000, currentRate: 0.05, avgTaxUSD: 145 },
  { id: 'grand-gedeh', name: 'Grand Gedeh', properties: 26000, currentRate: 0.05, avgTaxUSD: 155 },
  { id: 'maryland', name: 'Maryland', properties: 28000, currentRate: 0.06, avgTaxUSD: 165 },
  { id: 'river-gee', name: 'River Gee', properties: 15000, currentRate: 0.03, avgTaxUSD: 115 },
  { id: 'bomi', name: 'Bomi', properties: 19000, currentRate: 0.04, avgTaxUSD: 135 },
];

const TIMELINE_OPTIONS = [
  { value: 1, label: '1 Year' },
  { value: 2, label: '2 Years' },
  { value: 3, label: '3 Years' },
  { value: 5, label: '5 Years' },
];

const SCENARIO_PRESETS = [
  { label: 'Pilot Focus', counties: ['montserrado', 'margibi', 'grand-bassa'], targetRate: 0.45, timeline: 2, color: '#fbbf24' },
  { label: 'Urban Sweep', counties: ['montserrado', 'margibi', 'bong', 'nimba'], targetRate: 0.60, timeline: 3, color: '#34d399' },
  { label: 'National Rollout', counties: ALL_COUNTIES.map(c => c.id), targetRate: 0.75, timeline: 5, color: '#60a5fa' },
];

function fmt(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-xs" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
      <p className="font-semibold mb-1" style={{ color: 'var(--dark-text)' }}>{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {fmt(p.value)}
        </p>
      ))}
    </div>
  );
};

export default function WhatIfScenarioTool() {
  const [selectedCounties, setSelectedCounties] = useState<string[]>(['montserrado', 'margibi', 'grand-bassa']);
  const [targetRate, setTargetRate] = useState(0.45);
  const [timeline, setTimeline] = useState(2);
  const [showAllCounties, setShowAllCounties] = useState(false);
  const [activePreset, setActivePreset] = useState<string | null>('Pilot Focus');

  const toggleCounty = (id: string) => {
    setActivePreset(null);
    setSelectedCounties(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const applyPreset = (preset: typeof SCENARIO_PRESETS[0]) => {
    setSelectedCounties(preset.counties);
    setTargetRate(preset.targetRate);
    setTimeline(preset.timeline);
    setActivePreset(preset.label);
  };

  const results = useMemo(() => {
    const chosen = ALL_COUNTIES.filter(c => selectedCounties.includes(c.id));
    const currentRevenue = chosen.reduce((sum, c) => sum + c.properties * c.currentRate * c.avgTaxUSD, 0);
    const projectedRevenue = chosen.reduce((sum, c) => sum + c.properties * targetRate * c.avgTaxUSD, 0);
    const revenueGain = projectedRevenue - currentRevenue;
    const newRegistrations = chosen.reduce((sum, c) => sum + Math.round(c.properties * (targetRate - c.currentRate)), 0);
    const annualGain = revenueGain;
    const totalGain = annualGain * timeline;

    // Year-by-year ramp
    const yearlyData = Array.from({ length: timeline }, (_, i) => {
      const yr = i + 1;
      const rampRate = c => c.currentRate + (targetRate - c.currentRate) * (yr / timeline);
      const yrRevenue = chosen.reduce((sum, c) => sum + c.properties * rampRate(c) * c.avgTaxUSD, 0);
      return { year: `Year ${yr}`, projected: Math.round(yrRevenue), current: Math.round(currentRevenue) };
    });

    // Per-county breakdown
    const countyBreakdown = chosen.map(c => ({
      name: c.name.length > 10 ? c.name.slice(0, 9) + '…' : c.name,
      current: Math.round(c.properties * c.currentRate * c.avgTaxUSD),
      projected: Math.round(c.properties * targetRate * c.avgTaxUSD),
      gain: Math.round(c.properties * (targetRate - c.currentRate) * c.avgTaxUSD),
    }));

    return { currentRevenue, projectedRevenue, revenueGain, newRegistrations, totalGain, yearlyData, countyBreakdown };
  }, [selectedCounties, targetRate, timeline]);

  const displayedCounties = showAllCounties ? ALL_COUNTIES : ALL_COUNTIES.slice(0, 8);

  return (
    <div
      className="rounded-xl p-5"
      style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' }}
          >
            <Sliders size={17} style={{ color: '#fff' }} />
          </div>
          <div>
            <h2 className="text-base font-bold" style={{ color: 'var(--dark-text)' }}>
              What-If Rollout Scenario Tool
            </h2>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
              Model registration strategies and see revenue impact in real time
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'rgba(168,85,247,0.15)', color: '#a855f7', border: '1px solid rgba(168,85,247,0.3)' }}>
          <Zap size={11} />
          Live Calc
        </div>
      </div>

      {/* Preset Scenarios */}
      <div className="mb-5">
        <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>QUICK SCENARIOS</p>
        <div className="flex flex-wrap gap-2">
          {SCENARIO_PRESETS.map(preset => (
            <button
              key={preset.label}
              onClick={() => applyPreset(preset)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
              style={{
                backgroundColor: activePreset === preset.label ? preset.color + '25' : 'var(--dark-surface)',
                color: activePreset === preset.label ? preset.color : 'var(--dark-muted)',
                border: `1px solid ${activePreset === preset.label ? preset.color + '60' : 'var(--dark-border)'}`,
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
        {/* Controls */}
        <div className="xl:col-span-2 space-y-4">
          {/* County Selection */}
          <div>
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>
              SELECT COUNTIES ({selectedCounties.length} selected)
            </p>
            <div className="flex flex-wrap gap-1.5">
              {displayedCounties.map(c => {
                const active = selectedCounties.includes(c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => toggleCounty(c.id)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all"
                    style={{
                      backgroundColor: active ? 'rgba(168,85,247,0.2)' : 'var(--dark-surface)',
                      color: active ? '#a855f7' : 'var(--dark-muted)',
                      border: `1px solid ${active ? 'rgba(168,85,247,0.5)' : 'var(--dark-border)'}`,
                    }}
                  >
                    <MapPin size={10} />
                    {c.name}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setShowAllCounties(v => !v)}
              className="mt-2 flex items-center gap-1 text-xs"
              style={{ color: 'var(--dark-muted)' }}
            >
              {showAllCounties ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {showAllCounties ? 'Show less' : `Show all 15 counties`}
            </button>
          </div>

          {/* Target Registration Rate */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-semibold" style={{ color: 'var(--dark-muted)' }}>TARGET REGISTRATION RATE</p>
              <span className="text-sm font-bold font-mono-data" style={{ color: '#a855f7' }}>
                {(targetRate * 100).toFixed(0)}%
              </span>
            </div>
            <input
              type="range"
              min={0.05}
              max={1}
              step={0.01}
              value={targetRate}
              onChange={e => { setTargetRate(parseFloat(e.target.value)); setActivePreset(null); }}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
              style={{ accentColor: '#a855f7' }}
            />
            <div className="flex justify-between text-xs mt-1" style={{ color: 'var(--dark-muted)' }}>
              <span>5%</span><span>Current: 11%</span><span>100%</span>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>ROLLOUT TIMELINE</p>
            <div className="flex gap-2">
              {TIMELINE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => { setTimeline(opt.value); setActivePreset(null); }}
                  className="flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all"
                  style={{
                    backgroundColor: timeline === opt.value ? 'rgba(168,85,247,0.2)' : 'var(--dark-surface)',
                    color: timeline === opt.value ? '#a855f7' : 'var(--dark-muted)',
                    border: `1px solid ${timeline === opt.value ? 'rgba(168,85,247,0.5)' : 'var(--dark-border)'}`,
                  }}
                >
                  <Calendar size={10} className="inline mr-1" />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 gap-2 pt-1">
            {[
              { label: 'Current Revenue', value: fmt(results.currentRevenue), color: 'var(--dark-muted)', sub: 'annual' },
              { label: 'Projected Revenue', value: fmt(results.projectedRevenue), color: '#a855f7', sub: 'annual' },
              { label: 'Revenue Gain', value: fmt(results.revenueGain), color: '#34d399', sub: 'per year' },
              { label: 'New Registrations', value: results.newRegistrations.toLocaleString(), color: '#fbbf24', sub: 'properties' },
            ].map(k => (
              <div
                key={k.label}
                className="rounded-lg px-3 py-2.5"
                style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
              >
                <p className="text-xs mb-0.5" style={{ color: 'var(--dark-muted)' }}>{k.label}</p>
                <p className="text-base font-bold font-mono-data leading-tight" style={{ color: k.color }}>{k.value}</p>
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{k.sub}</p>
              </div>
            ))}
          </div>

          {/* Total gain callout */}
          <div
            className="rounded-lg px-4 py-3 flex items-center justify-between"
            style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.15) 0%, rgba(124,58,237,0.1) 100%)', border: '1px solid rgba(168,85,247,0.3)' }}
          >
            <div>
              <p className="text-xs font-semibold" style={{ color: '#a855f7' }}>
                {timeline}-Year Total Revenue Gain
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
                {selectedCounties.length} counties · {(targetRate * 100).toFixed(0)}% target rate
              </p>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold font-mono-data" style={{ color: '#a855f7' }}>{fmt(results.totalGain)}</p>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>cumulative</p>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="xl:col-span-3 space-y-4">
          {/* Year-by-year projection */}
          <div>
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>
              REVENUE PROJECTION — YEAR BY YEAR
            </p>
            <div style={{ height: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={results.yearlyData} barGap={4} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="year" tick={{ fill: 'var(--dark-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={v => fmt(v)} tick={{ fill: 'var(--dark-muted)', fontSize: 10 }} axisLine={false} tickLine={false} width={55} />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={results.currentRevenue} stroke="rgba(255,255,255,0.2)" strokeDasharray="4 4" label={{ value: 'Baseline', fill: 'var(--dark-muted)', fontSize: 10 }} />
                  <Bar dataKey="current" name="Baseline" fill="rgba(255,255,255,0.08)" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="projected" name="Projected" fill="#a855f7" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Per-county breakdown */}
          <div>
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>
              REVENUE GAIN BY COUNTY
            </p>
            {results.countyBreakdown.length === 0 ? (
              <div className="flex items-center justify-center h-28 rounded-lg" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Select at least one county to see breakdown</p>
              </div>
            ) : (
              <div style={{ height: Math.min(results.countyBreakdown.length * 36 + 20, 220) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={results.countyBreakdown} layout="vertical" barGap={2} barCategoryGap="25%">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                    <XAxis type="number" tickFormatter={v => fmt(v)} tick={{ fill: 'var(--dark-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" tick={{ fill: 'var(--dark-muted)', fontSize: 10 }} axisLine={false} tickLine={false} width={72} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="current" name="Current" fill="rgba(255,255,255,0.1)" radius={[0, 2, 2, 0]} />
                    <Bar dataKey="projected" name="Projected" radius={[0, 2, 2, 0]}>
                      {results.countyBreakdown.map((_, i) => (
                        <Cell key={i} fill={`hsl(${270 + i * 8}, 70%, 65%)`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Summary table */}
          {results.countyBreakdown.length > 0 && (
            <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--dark-border)' }}>
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ backgroundColor: 'var(--dark-surface)' }}>
                    {['County', 'Current', 'Projected', 'Gain'].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.countyBreakdown.map((row, i) => (
                    <tr key={row.name} style={{ backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)', borderTop: '1px solid var(--dark-border)' }}>
                      <td className="px-3 py-2 font-medium" style={{ color: 'var(--dark-text)' }}>{row.name}</td>
                      <td className="px-3 py-2 font-mono-data" style={{ color: 'var(--dark-muted)' }}>{fmt(row.current)}</td>
                      <td className="px-3 py-2 font-mono-data" style={{ color: '#a855f7' }}>{fmt(row.projected)}</td>
                      <td className="px-3 py-2 font-mono-data font-semibold" style={{ color: '#34d399' }}>+{fmt(row.gain)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
